import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp, ParallaxImage } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";
import { IMAGES } from "@/constants/site";

const CONTENT = {
  en: {
    eyebrow: "About me",
    h1a: "Hello — I'm ",
    h1name: "Ioannis Tsiros",
    h1b: ".",
    subline: "MBACP · Individual psychotherapist working with adults in Athens and online.",
    regTitle: "Registrations",
    registrations: [
      { body: "BACP — British Association for Counselling & Psychotherapy", ref: "United Kingdom · Registration 420016" },
      { body: "Licensed Psychologist", ref: "Greece · Registration 274123" },
      { body: "British Psychological Society — Member", ref: "Membership 840906" },
    ],
    eduTitle: "Education",
    education: [
      { body: "MSc Counseling and Psychotherapy", ref: "The American University of Greece" },
      { body: "BSc (Hons) Psychology", ref: "The Open University, United Kingdom" },
    ],
    sections: [
      {
        title: "My Background",
        body: [
          "My name is Ioannis Tsiros, and I am a licensed psychologist and psychotherapist. I hold a license to practice in both Greece and the UK, where I am a registered member of the British Association for Counselling and Psychotherapy (MBACP). Through my private practice, I provide online and individual sessions, focusing on creating a safe and open space for every client.",
          "Beyond my private practice, I have collaborated with a number of organisations — such as the Counseling Center of ACG and the municipal clinics of the Municipality of Athens — and have completed programmes in hospital clinics. These gave me the experience of working with clients across a wide spectrum of concerns: addictions, anxiety, depression, family relationship issues, domestic violence, grief, survivors of abuse, identity crisis, trauma, and eating disorders.",
        ],
      },
      {
        title: "My Therapeutic Approach",
        body: [
          "In my practice, I work primarily phenomenologically, situating my work within the broader field of post-existential and analytic psychotherapy. My approach is deeply influenced by the thought of Freud, Lacan, Derrida, and Heidegger, as well as by relational psychoanalysis. I consciously move away from pathologizing labels and strict medical models. I believe that human distress is not a sterile diagnosis, but an experience in search of meaning. Therefore, the therapy I offer is grounded in human connection, relational dialogue, and the use of everyday, unpretentious language, steering clear of terminology that often alienates.",
          "In practice, that means our sessions are collaborative and conversational. I will not hand you a manual. We pay attention to what you bring, how you relate, and what a more honest, freer life might look like for you.",
        ],
      },
      {
        title: "Professional Journey & Development",
        body: [
          "With over twenty years of professional experience in the labor market and extensive therapeutic experience, I approach the challenges of my clients with a deep understanding of the demands of modern life. My commitment to psychotherapy is continuous, as I actively participate in advanced therapeutic training and supervision programs to keep my practice alive, contemporary, and, above all, profoundly human.",
          "As a researcher-psychologist, I have also participated in international conferences and presented my own research papers. I believe in the continuous education and supervision of therapists, which is why I strive to stay up to date with the latest research in the areas that concern my work.",
        ],
      },
    ],
    deepTitle: "My Approach in Depth",
    deepIntro: "My psychotherapeutic approach constitutes a powerful synthesis of existential philosophy and relational psychoanalysis, enriched by Lacanian theory and postmodern thought. In this model, I do not view the individual as an isolated mind struggling with internal drives, but as a being that exists, takes shape, and makes sense of life strictly through their relationships and language.",
    rootsTitle: "Philosophical & Theoretical Roots",
    roots: [
      { title: "Existentialism", body: "Drawing on thinkers like Kierkegaard, Nietzsche, Sartre, and Camus, my practice centers on individual freedom, responsibility, the anxiety of choice, and the human need to create meaning in an uncertain world." },
      { title: "Phenomenology", body: "Informed by Heidegger, I place emphasis on how the individual subjectively experiences reality. The central concept of Dasein (\u201Cbeing-in-the-world\u201D) is interpreted as being inextricably linked to \u201Cbeing-with-others\u201D. Existence is, by definition, co-existence." },
      { title: "Postmodernism", body: "Incorporating postmodern critique, I approach identity not as a fixed and given entity, but as a fluid construct continually co-created through social narratives. We move away from the search for a rigid, objective \u201Ctruth\u201D or the absolute authority of the therapist, focusing instead on deconstructing entrenched beliefs and making space for the multiplicity of the client's lived experiences." },
    ],
    inflTitle: "Influences: Relational Psychoanalysis & Lacanian Perspective",
    influences: [
      { title: "The Lacanian Influence", body: "Lacan reminds us that the unconscious is structured like a language. In therapy, we listen closely to the client's discourse and signifiers. Existential anxiety meets the Lacanian \u201Clack\u201D (manque) — that fundamental absence which mobilizes our desire — while we explore how the individual has been structured through the gaze and expectations of the (big) \u201COther\u201D." },
      { title: "Intersubjectivity", body: "Therapy is not a process where I, as a \u201Cneutral\u201D analyst, simply observe the patient's projections. Instead, it is a shared, co-created field. I bring my own subjectivity and participate actively, focusing on what happens in the \u201Chere and now\u201D and in the \u201Cin-between\u201D of our encounter." },
      { title: "The Authentic Encounter", body: "Also influenced by the philosophy of Martin Buber (the I-Thou relationship), I view the therapeutic relationship itself — a deep, authentic, and human connection — as the primary therapeutic tool, rather than just a vehicle for interpreting the past." },
      { title: "Defenses as Relational Patterns", body: "The unconscious and psychological defenses are redefined. Defenses (e.g., avoidance, control) are understood as relational strategies the individual developed to protect themselves from the pain of universal existential givens (such as existential isolation, mortality, or the burden of freedom). The way the client defends against these givens is reproduced and analyzed live within our therapeutic relationship." },
    ],
    deepClose: "In essence, my approach postulates that our existential traumas arise within relationships (or their absence) and are woven into language. Therefore, it is only within a new, authentic, intersubjective relational experience that they can be healed and re-signified.",
    philosophyTitle: "Philosophy of care",
    philosophy: [
      { title: "People, not pathologies", body: "You are not a diagnosis. We start from your lived experience and what matters to you, and work from there." },
      { title: "Understanding before advice", body: "Lasting change grows from being genuinely understood — not from being told what to do." },
      { title: "Relationship as method", body: "How we meet in the room mirrors how you meet life. It is both the subject and the vehicle of the work." },
      { title: "Freedom and responsibility", body: "A post-existential lens takes questions of meaning, choice and mortality seriously — gently, and together." },
    ],
    ctaText: "The first conversation is free — and there's no obligation.",
    ctaBtn: "Book a Consultation",
  },
  el: {
    eyebrow: "Για εμένα",
    h1a: "Γεια σας — είμαι ο ",
    h1name: "Ιωάννης Τσίρος",
    h1b: ".",
    subline: "MBACP · Ατομική ψυχοθεραπεία με ενήλικες — στην Αθήνα και online.",
    regTitle: "Πιστοποιήσεις",
    registrations: [
      { body: "BACP — British Association for Counselling & Psychotherapy", ref: "Ηνωμένο Βασίλειο · Μητρώο 420016" },
      { body: "Πτυχιούχος Ψυχολόγος", ref: "Ελλάδα · Μητρώο 274123" },
      { body: "British Psychological Society — Μέλος", ref: "Μητρώο Μέλους 840906" },
    ],
    eduTitle: "Σπουδές",
    education: [
      { body: "MSc Counseling and Psychotherapy", ref: "The American University of Greece" },
      { body: "BSc (Hons) Psychology", ref: "The Open University, Ηνωμένο Βασίλειο" },
    ],
    sections: [
      {
        title: "Το υπόβαθρό μου",
        body: [
          "Ονομάζομαι Ιωάννης Τσίρος και είμαι αδειούχος ψυχολόγος και ψυχοθεραπευτής. Διατηρώ άδεια ασκήσεως επαγγέλματος τόσο στην Ελλάδα όσο και στο Ηνωμένο Βασίλειο, όπου είμαι εγγεγραμμένο μέλος του Βρετανικού Συλλόγου Συμβουλευτικής και Ψυχοθεραπείας (MBACP). Μέσα από το ιδιωτικό μου γραφείο, παρέχω διαδικτυακές και ατομικές συνεδρίες, εστιάζοντας στη δημιουργία ενός ασφαλούς και ανοιχτού χώρου για κάθε θεραπευόμενο.",
          "Εκτός από την ιδιωτική μου πρακτική, έχω συνεργαστεί με πολλούς οργανισμούς (όπως το Κέντρο Συμβουλευτικής του ACG και τα δημοτικά ιατρεία του Δήμου Αθηναίων), ενώ έχω παρακολουθήσει προγράμματα σε νοσοκομειακές κλινικές. Αυτά μου παρείχαν την εμπειρία συνεργασίας με πελάτες με ένα ευρύ φάσμα θεμάτων, όπως εξαρτήσεις, άγχος, κατάθλιψη, ζητήματα οικογενειακών σχέσεων, ενδοοικογενειακή βία, πένθος, θύματα κακοποίησης, κρίση ταυτότητας, τραύματα και διατροφικές διαταραχές.",
        ],
      },
      {
        title: "Η Θεραπευτική μου Προσέγγιση",
        body: [
          "Στη δουλειά μου εργάζομαι κυρίως φαινομενολογικά, τοποθετώντας την πρακτική μου στον ευρύτερο χώρο της μετα-υπαρξιακής και αναλυτικής ψυχοθεραπείας. Αντλώ βαθιές επιρροές από τη σκέψη των Freud, Lacan, Derrida και Heidegger, καθώς και από τη σχεσιακή ψυχανάλυση (relational psychoanalysis). Απομακρύνομαι συνειδητά από τις παθολογικοποιητικές ταμπέλες και τα αυστηρά ιατρικά μοντέλα. Πιστεύω ότι η ανθρώπινη δυσφορία δεν αποτελεί μια στείρα διάγνωση, αλλά μια εμπειρία που αναζητά νόημα. Γι' αυτό, η θεραπεία που προτείνω βασίζεται στην ανθρώπινη σύνδεση, στον σχεσιακό διάλογο και στη χρήση καθημερινής, ανεπιτήδευτης γλώσσας, μακριά από ορολογίες που συχνά αποξενώνουν.",
          "Στην πράξη, αυτό σημαίνει ότι οι συνεδρίες μας είναι συνεργατικές και διαλεκτικές. Δεν θα σας παραδώσω κάποιο εγχειρίδιο. Προσέχουμε ό,τι φέρνετε, τον τρόπο που σχετίζεστε, και πώς μπορεί να μοιάζει μια πιο ειλικρινής, πιο ελεύθερη ζωή για εσάς.",
        ],
      },
      {
        title: "Επαγγελματική Πορεία & Εξέλιξη",
        body: [
          "Με περισσότερα από είκοσι χρόνια πορείας στην αγορά εργασίας και εκτενή θεραπευτική εμπειρία, προσεγγίζω τις προκλήσεις των θεραπευόμενών μου έχοντας βαθιά κατανόηση των απαιτήσεων της σύγχρονης ζωής. Η δέσμευσή μου στην ψυχοθεραπεία είναι διαρκής, συμμετέχοντας ενεργά σε προγράμματα προηγμένης θεραπευτικής εκπαίδευσης και εποπτείας, προκειμένου να διατηρώ την πρακτική μου ζωντανή, σύγχρονη και, πάνω από όλα, βαθιά ανθρώπινη.",
          "Επίσης, ως ερευνητής ψυχολόγος, έχω συμμετάσχει σε διεθνή συνέδρια και έχω παρουσιάσει δικές μου ερευνητικές εργασίες. Πιστεύω στη διαρκή εκπαίδευση και εποπτεία των θεραπευτών, για αυτό επιδιώκω να ενημερώνομαι για τις τελευταίες έρευνες στα θέματα που με απασχολούν.",
        ],
      },
    ],
    deepTitle: "Η Προσέγγισή μου σε Βάθος",
    deepIntro: "Η ψυχοθεραπευτική μου προσέγγιση συνιστά μια ισχυρή σύνθεση της υπαρξιακής φιλοσοφίας με τη σχεσιακή ψυχανάλυση (relational psychoanalysis), εμπλουτισμένη με εργαλεία από τη λακανική θεωρία και τον μεταμοντέρνο στοχασμό. Σε αυτό το μοντέλο, δεν προσεγγίζω το άτομο ως έναν απομονωμένο νου που παλεύει με εσωτερικές ενορμήσεις, αλλά ως ένα ον που υπάρχει, διαμορφώνεται και νοηματοδοτεί τη ζωή του αυστηρά μέσα από τις σχέσεις του και τον λόγο.",
    rootsTitle: "Φιλοσοφικές & Θεωρητικές Ρίζες",
    roots: [
      { title: "Υπαρξισμός", body: "Με αφετηρία στοχαστές όπως οι Kierkegaard, Nietzsche, Sartre και Camus, η δουλειά μου θέτει στο επίκεντρο την ατομική ελευθερία, την ευθύνη, την αγωνία της επιλογής και την ανάγκη του ανθρώπου να δημιουργήσει νόημα σε έναν αβέβαιο κόσμο." },
      { title: "Φαινομενολογία", body: "Αντλώντας από τον Heidegger, δίνω έμφαση στο πώς το άτομο βιώνει υποκειμενικά την πραγματικότητα. Η κεντρική έννοια του Dasein («είναι-στον-κόσμο») ερμηνεύεται άρρηκτα συνδεδεμένη με το «είναι-μαζί-με-τους-άλλους». Η ύπαρξη είναι εξ ορισμού συν-ύπαρξη." },
      { title: "Μεταμοντερνισμός", body: "Ενσωματώνοντας τη μεταμοντέρνα κριτική, αντιμετωπίζω την ταυτότητα όχι ως κάτι σταθερό και δεδομένο, αλλά ως κάτι ρευστό, το οποίο συν-κατασκευάζεται διαρκώς μέσα από κοινωνικές αφηγήσεις. Απομακρυνόμαστε από την αναζήτηση μιας αυστηρής, αντικειμενικής «αλήθειας» ή την αυθεντία του θεραπευτή, εστιάζοντας στην αποδόμηση των παγιωμένων πεποιθήσεων και δίνοντας χώρο στην πολλαπλότητα των βιωμάτων του θεραπευόμενου." },
    ],
    inflTitle: "Επιρροές: Σχεσιακή Ψυχανάλυση & Λακανική Οπτική",
    influences: [
      { title: "Η Λακανική Επιρροή", body: "Ο Lacan μάς υπενθυμίζει ότι το ασυνείδητο είναι δομημένο σαν γλώσσα. Μέσα στη θεραπεία, ακούμε προσεκτικά τον λόγο και τα σημαίνοντα του θεραπευόμενου. Η υπαρξιακή αγωνία συναντά το λακανικό «έλλειμμα» (manque) — τη θεμελιώδη έλλειψη που κινητοποιεί την επιθυμία μας — ενώ διερευνούμε πώς το άτομο έχει δομηθεί μέσα από το βλέμμα και τις προσδοκίες του (μεγάλου) «Άλλου»." },
      { title: "Δι-υποκειμενικότητα (Intersubjectivity)", body: "Η θεραπεία δεν είναι μια διαδικασία όπου ως «ουδέτερος» αναλυτής παρατηρώ τις προβολές του ασθενούς. Αντιθέτως, αποτελεί ένα κοινό, συνδημιουργούμενο πεδίο. Φέρνω τη δική μου υποκειμενικότητα και μετέχω ενεργά, εστιάζοντας στο τι συμβαίνει στο «εδώ και τώρα» και στο «ανάμεσα» μας." },
      { title: "Η Αυθεντική Συνάντηση", body: "Επηρεασμένη και από τη φιλοσοφία του Martin Buber (σχέση Εγώ-Εσύ), βλέπω την ίδια τη θεραπευτική σχέση — μια σχέση βαθιά, αυθεντική και ανθρώπινη — ως το κατεξοχήν θεραπευτικό εργαλείο, και όχι απλώς ως όχημα για την ερμηνεία του παρελθόντος." },
      { title: "Οι Άμυνες ως Σχεσιακά Μοτίβα", body: "Το ασυνείδητο και οι ψυχολογικές άμυνες επαναπροσδιορίζονται. Οι άμυνες (π.χ. αποφυγή, έλεγχος) κατανοούνται ως σχεσιακοί τρόποι που ανέπτυξε το άτομο για να προστατευτεί από τον πόνο των καθολικών υπαρξιακών δεδομένων (την υπαρξιακή απομόνωση, τη θνητότητα, το άχθος της ελευθερίας). Ο τρόπος που ο θεραπευόμενος αμύνεται, αναπαράγεται και αναλύεται ζωντανά μέσα στη θεραπευτική μας σχέση." },
    ],
    deepClose: "Ουσιαστικά, η προσέγγισή μου πρεσβεύει ότι τα υπαρξιακά μας τραύματα προκύπτουν μέσα σε σχέσεις (ή στην απουσία αυτών) και πλέκονται μέσα στη γλώσσα. Συνεπώς, μόνο μέσα σε μια νέα, αυθεντική, δι-υποκειμενική εμπειρία μπορούν να επουλωθούν και να αποκτήσουν νέο νόημα.",
    philosophyTitle: "Φιλοσοφία φροντίδας",
    philosophy: [
      { title: "Άνθρωποι, όχι διαγνώσεις", body: "Δεν είστε μια διάγνωση. Ξεκινάμε από τη βιωμένη σας εμπειρία και από ό,τι σας νοιάζει, και δουλεύουμε από εκεί." },
      { title: "Κατανόηση πριν τις συμβουλές", body: "Η διαρκής αλλαγή μεγαλώνει από τη γνήσια κατανόηση — όχι από το να σας λένε τι να κάνετε." },
      { title: "Η σχέση ως μέθοδος", body: "Ο τρόπος που συναντιόμαστε στη συνεδρία αντικατοπτρίζει τον τρόπο που συναντάτε τη ζωή. Είναι ταυτόχρονα το θέμα και το μέσο της δουλειάς." },
      { title: "Ελευθερία και Ευθύνη", body: "Μια μετα-υπαρξιακή οπτική παίρνει σοβαρά τα ερωτήματα του νοήματος, της επιλογής και της ελεύθερίας — ειλικρινά, και πάντα μαζί." },
    ],
    ctaText: "Η πρώτη γνωριμία είναι δωρεάν — και χωρίς καμία δέσμευση.",
    ctaBtn: "Κλείστε Ραντεβού",
  },
};

const About = () => {
  const { lang } = useLang();
  const c = CONTENT[lang];
  useSeo("about");

  return (
    <div data-testid="page-about">
      <section className="container-x pb-24 pt-32 md:pt-40 lg:pt-44">
        <FadeUp>
          <p className="eyebrow mb-6">{c.eyebrow}</p>
          <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.05] tracking-tight text-sage-900 sm:text-6xl">
            {c.h1a}
            <em className="italic text-sage-600">{c.h1name}</em>
            {c.h1b}
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">{c.subline}</p>
        </FadeUp>

        <div className="mt-16 grid gap-14 lg:mt-24 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="space-y-8 lg:sticky lg:top-28">
              <ParallaxImage
                src="/yannis-portrait.webp"
                alt="Portrait of Ioannis Tsiros"
                className="aspect-[4/5] rounded-3xl"
                distance={35}
              />
              <div className="grain-card bg-card p-8">
                <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-sage-600">
                  <BadgeCheck className="h-4 w-4 text-clay" /> {c.regTitle}
                </h2>
                <ul className="mt-5 space-y-4">
                  {c.registrations.map((r) => (
                    <li key={r.body} className="text-sm leading-relaxed">
                      <span className="font-medium text-foreground">{r.body}</span>
                      <span className="block text-muted-foreground">{r.ref}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 rounded-2xl bg-white p-4 ring-1 ring-sage-600/10" data-testid="about-bacp-logo">
                  <img
                    src="/bacp-logo.webp"
                    alt="BACP Registered Member 420016 — MBACP"
                    className="h-16 w-auto"
                    loading="lazy"
                  />
                </div>
                <h2 className="mt-8 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-sage-600">
                  <GraduationCap className="h-4 w-4 text-clay" /> {c.eduTitle}
                </h2>
                <ul className="mt-5 space-y-4">
                  {c.education.map((d) => (
                    <li key={d.body} className="text-sm leading-relaxed">
                      <span className="font-medium text-foreground">{d.body}</span>
                      <span className="block text-muted-foreground">{d.ref}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-14 lg:col-span-7">
            {c.sections.map((s) => (
              <FadeUp key={s.title}>
                <section className="border-t border-sage-600/10 pt-10">
                  <h2 className="font-display text-3xl font-light tracking-tight text-sage-900">{s.title}</h2>
                  <div className="mt-6 space-y-5">
                    {s.body.map((p) => (
                      <p key={p.slice(0, 24)} className="leading-relaxed text-foreground/80">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              </FadeUp>
            ))}

            <FadeUp>
              <section className="border-t border-sage-600/10 pt-10" data-testid="about-deep-approach">
                <h2 className="font-display text-3xl font-light tracking-tight text-sage-900">{c.deepTitle}</h2>
                <p className="mt-6 leading-relaxed text-foreground/80">{c.deepIntro}</p>

                <h3 className="mt-10 font-display text-2xl font-light text-sage-900">{c.rootsTitle}</h3>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  {c.roots.map((p, i) => (
                    <div key={p.title} className="rounded-2xl bg-secondary/70 p-6">
                      <span className="font-display text-sm italic text-clay">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h4 className="mt-3 font-medium text-sage-800">{p.title}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                    </div>
                  ))}
                </div>

                <h3 className="mt-10 font-display text-2xl font-light text-sage-900">{c.inflTitle}</h3>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  {c.influences.map((p, i) => (
                    <div key={p.title} className="rounded-2xl bg-secondary/70 p-6">
                      <span className="font-display text-sm italic text-clay">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h4 className="mt-3 font-medium text-sage-800">{p.title}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                    </div>
                  ))}
                </div>

                <p className="mt-10 border-l-2 border-clay/60 pl-6 font-display text-xl italic leading-relaxed text-sage-800">
                  {c.deepClose}
                </p>
              </section>
            </FadeUp>

            <FadeUp>
              <section className="border-t border-sage-600/10 pt-10">
                <h2 className="font-display text-3xl font-light tracking-tight text-sage-900">
                  {c.philosophyTitle}
                </h2>
                <div className="mt-8 grid gap-10 lg:grid-cols-12">
                  <div className="lg:col-span-4">
                    <figure className="lg:sticky lg:top-28">
                      <ParallaxImage
                        src="/the-kiss.jpg"
                        alt="Gustav Klimt, The Kiss (1908)"
                        className="aspect-[3/4] rounded-b-3xl rounded-t-[180px]"
                        distance={25}
                      />
                      <figcaption className="mt-3 font-display text-xs italic text-muted-foreground">
                        Gustav Klimt, The Kiss (1908)
                      </figcaption>
                    </figure>
                  </div>
                  <div className="grid content-start gap-6 sm:grid-cols-2 lg:col-span-8">
                    {c.philosophy.map((p, i) => (
                      <div key={p.title} className="rounded-2xl bg-secondary/70 p-6">
                        <span className="font-display text-sm italic text-clay">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className="mt-3 font-medium text-sage-800">{p.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </FadeUp>

            <FadeUp>
              <div className="grain-card flex flex-col items-start justify-between gap-6 bg-sage-600 p-10 text-background sm:flex-row sm:items-center">
                <p className="max-w-sm font-display text-2xl font-light leading-snug">{c.ctaText}</p>
                <Button
                  asChild
                  data-testid="about-cta-book"
                  className="rounded-full bg-background px-6 py-5 text-sage-700 hover:bg-clay-light"
                >
                  <Link to="/intake" className="inline-flex items-center gap-2">
                    {c.ctaBtn} <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
